import * as React from "react";
import { useEffect, useState } from "react";
import {
  makeStyles,
  tokens,
  Field,
  Input,
  Dropdown,
  Option,
  Combobox,
  Textarea,
  Text,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InfoLabel,
  Link,
  Switch,
  Spinner,
  Toast,
  ToastTitle,
  ToastBody,
  ToastTrigger,
  useToastController,
} from "@fluentui/react-components";
import {
  Eye16Regular,
  EyeOff16Regular,
  Dismiss16Regular,
  ErrorCircle20Filled,
} from "@fluentui/react-icons";
import { SmsType, Encoding, smsTypeOptions, encodingOptions } from "./types";
import { findCountry, searchCountries } from "./countries";
import { validateSenderId } from "./validation";
import { getAccountInfo, AccountInfo, Lox24ApiError } from "./lox24Api";
import { useSettings } from "./useSettings";
import { loadSettings, saveSettings } from "./settingsStorage";
import {
  enableCalendarAutomation,
  disableCalendarAutomation,
  getAutomationStatus,
  DisableResult,
  AutomationStatus,
} from "../calendar/calendarAutomationApi";
import {
  syncSettingsToBackend,
  syncIfAutomationEnabled,
  getLastSyncedAt,
  clearLastSyncedAt,
} from "./settingsSyncApi";
import { describeAuthError } from "../auth/authDiagnostics";
import { ScrollFade, useScrollFade } from "../../components/ScrollFade";
import { TOASTER_ID } from "../../toaster";
import { useTranslation, Trans } from "react-i18next";
import type { TFunction } from "i18next";
import { i18n } from "../../i18n";
import { formatDateTime } from "../../i18n/format";

// Same origin as the add-in, so it follows whatever host the manifest was
// built for rather than hardcoding one. ?lang= carries Outlook's language, which
// the guide would otherwise guess from the browser (pt-BR Outlook, en browser).
const userGuideUrl = (): string =>
  `${window.location.origin}/user-guide?lang=${encodeURIComponent(i18n.language)}#automated`;

const useStyles = makeStyles({
  panel: { display: "flex", flexDirection: "column", flexGrow: 1, minHeight: 0 },
  fields: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    overflowY: "auto",
    flexGrow: 1,
    minHeight: 0,
    padding: "12px",
  },
  footer: {
    display: "flex",
    padding: "10px 12px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    flexShrink: 0,
  },
  actions: { display: "flex", gap: "8px", width: "100%" },
  testButton: { flex: "1 1 30%", minWidth: "fit-content" },
  saveButton: { flex: "1 1 70%" },
  statusProblem: {
    display: "block",
    marginTop: "6px",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteDarkOrangeForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  statusOk: {
    display: "block",
    marginTop: "6px",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteGreenForeground1,
  },
  syncNote: {
    display: "block",
    marginTop: "6px",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  // Content of the (i) popover beside the switch. The #number convention is the
  // one thing a user has to learn and it appears nowhere else in the UI.
  howToStack: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    maxWidth: "260px",
  },
  howToText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  // The example sits on its own line so it reads as something to copy rather
  // than more prose. Emphasis on the #number comes from weight, not colour:
  // nothing in this block is brand red, because red here means the Save button
  // and the colour warnings appear in.
  howToExample: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    padding: "6px 8px",
    wordBreak: "break-word",
  },
  howToTag: { fontWeight: tokens.fontWeightSemibold },
  // Underlined neutral rather than brand red: in this panel red is the Save
  // button and the colour warnings appear in, so a red link reads as an error.
  // The underline carries the affordance instead of colour.
  howToLink: {
    alignSelf: "flex-start",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    textDecorationLine: "underline",
    ":hover": { color: tokens.colorNeutralForeground1 },
  },
  loading: { display: "flex", justifyContent: "center", padding: "24px" },
  toastBody: { gridColumnStart: "1", gridColumnEnd: "-1" },
});

/** How to actually trigger an automated message — the (i) popover's content. */
const HowToUse: React.FC<{ styles: Record<string, string> }> = ({ styles }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.howToStack}>
      <Text className={styles.howToText}>
        {/* <Trans> keeps the <strong> and <code> emphasis inside a single
            sentence, so translators move the markup with the words instead of
            working on fragments that only assemble correctly in English. */}
        <Trans i18nKey="settings.howTo.intro" components={{ strong: <strong />, code: <code /> }} />
      </Text>
      <Text className={styles.howToExample}>
        {t("settings.howTo.example")} <span className={styles.howToTag}>#+491701234567</span>
      </Text>
      <Text className={styles.howToText}>
        <Trans i18nKey="settings.howTo.templates" components={{ code: <code /> }} />
      </Text>
      <Link className={styles.howToLink} href={userGuideUrl()} target="_blank" rel="noreferrer">
        {t("settings.howTo.link")}
      </Link>
    </div>
  );
};

/**
 * Localised text of the selected Dropdown option. Fluent wants the display text
 * separately from the value, so both paths translate through here.
 */
function optionLabel<T extends string>(
  translate: TFunction,
  options: readonly { value: T; labelKey: string }[],
  value: T
): string {
  const match = options.find((o) => o.value === value);
  return match ? (translate(match.labelKey) as string) : "";
}

/**
 * The status line for the last background send. The server sends a code, so the
 * wording follows the add-in's language; older rows only carry English `detail`.
 */
function automationDetail(translate: TFunction, status: AutomationStatus): string {
  if (status.reason) {
    return translate(`settings.automation.reason.${status.reason}`) as string;
  }
  return status.detail ?? "";
}

/** Tell the user exactly what turning automation off did to queued reminders. */
function describeDisableResult(result?: DisableResult): string {
  const cancelled = result?.cancelledMessages ?? 0;
  // Plural form comes from Intl.PluralRules — Polish has three.
  return cancelled > 0
    ? (i18n.t("settings.disable.cancelled", { count: cancelled }) as string)
    : (i18n.t("settings.disable.none") as string);
}

function accountDetail(info: AccountInfo): string {
  const parts: string[] = [];
  if (info.id) parts.push(i18n.t("settings.account.id", { id: info.id }) as string);
  if (typeof info.balance_amount === "number") {
    const amount = `${info.balance_amount}${info.currency ? " " + info.currency : ""}`;
    parts.push(i18n.t("settings.account.balance", { amount }) as string);
  }
  return parts.join(" · ");
}

interface SettingsPanelProps {
  onSaved?: () => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ onSaved }) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { settings, update, save } = useSettings();
  const { dispatchToast } = useToastController(TOASTER_ID);
  const [showKey, setShowKey] = useState(false);
  const [countryQuery, setCountryQuery] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [savingNow, setSavingNow] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [automationBusy, setAutomationBusy] = useState(false);
  const [syncedAt, setSyncedAt] = useState<string | undefined>(() => getLastSyncedAt());
  // Turning automation off cancels reminders that are already queued, so it
  // asks first rather than doing it silently.
  const [confirmDisable, setConfirmDisable] = useState(false);
  // How background sending is actually going. Fetched when the panel opens,
  // because a failing API key is otherwise invisible to the user.
  const [automation, setAutomation] = useState<AutomationStatus | undefined>();
  const { scrollRef, showFade, updateFade, scrollDown } = useScrollFade();

  // The form's height changes as validation messages and the country list appear.
  useEffect(updateFade, [settings, countryQuery, submitted, automationBusy, updateFade]);

  const automationOn = settings?.calendarAutomation ?? false;
  useEffect(() => {
    if (!automationOn) {
      setAutomation(undefined);
      return undefined;
    }
    let cancelled = false;
    void getAutomationStatus().then((s) => {
      if (!cancelled) setAutomation(s);
    });
    return () => {
      cancelled = true;
    };
  }, [automationOn]);

  const notify = (title: string, body: string, intent: "success" | "error" | "warning") =>
    dispatchToast(
      <Toast>
        <ToastTitle
          media={
            intent === "error" ? (
              <ErrorCircle20Filled style={{ color: tokens.colorStatusDangerForeground1 }} />
            ) : undefined
          }
          action={
            <ToastTrigger>
              <Button
                appearance="transparent"
                size="small"
                icon={<Dismiss16Regular />}
                aria-label={t("settings.close")}
              />
            </ToastTrigger>
          }
        >
          {title}
        </ToastTitle>
        {body && <ToastBody className={styles.toastBody}>{body}</ToastBody>}
      </Toast>,
      { intent }
    );

  if (!settings) {
    return (
      <div className={styles.loading}>
        <Spinner size="small" label={t("settings.loading")} />
      </div>
    );
  }

  const apiKeyError =
    submitted && !settings.apiKey.trim() ? t("settings.apiKey.required") : undefined;
  const senderIdError =
    submitted || settings.senderId.trim() ? validateSenderId(settings.senderId) : undefined;
  const selectedCountry = findCountry(settings.defaultCountry);
  const selectedCountryText = selectedCountry
    ? `${selectedCountry.name} (${selectedCountry.dialCode})`
    : "";
  const countryList = searchCountries(countryQuery ?? "");
  const busy = testing || savingNow;

  const testConnection = async () => {
    setTesting(true);
    try {
      const info = await getAccountInfo(settings.apiKey);
      notify(
        t("settings.toast.connected"),
        accountDetail(info) || t("settings.toast.keyValid"),
        "success"
      );
    } catch (err) {
      notify(t("settings.toast.connectionFailed"), (err as Error).message, "error");
    } finally {
      setTesting(false);
    }
  };

  const onSave = async () => {
    setSubmitted(true);
    if (!settings.apiKey.trim() || validateSenderId(settings.senderId)) {
      return;
    }

    setSavingNow(true);
    try {
      await getAccountInfo(settings.apiKey); // verify before persisting
    } catch (e) {
      const apiErr = e as Lox24ApiError;
      if (apiErr.status === 401) {
        notify(t("settings.toast.invalidKey"), t("settings.toast.invalidKeyBody"), "error");
        setSavingNow(false);
        return;
      }
      await save();
      notify(t("settings.toast.savedWarning"), t("settings.toast.unverified"), "warning");
      setSavingNow(false);
      onSaved?.();
      return;
    }

    await save();
    // With automation on, the server holds a copy for background sending — push
    // it, but never fail the local save over it.
    const syncError = await syncIfAutomationEnabled();
    setSyncedAt(getLastSyncedAt());
    setSavingNow(false);
    if (syncError) {
      notify(
        t("settings.toast.savedLocally"),
        t("settings.toast.savedLocallyBody", { reason: syncError }),
        "warning"
      );
    } else {
      notify(t("settings.toast.saved"), "", "success");
    }
    onSaved?.();
  };

  // The automation toggle acts immediately (creates/deletes the server-side
  // Graph subscription) rather than waiting for Save. Persist the flag against
  // the last-saved settings so unsaved form edits aren't written.
  const onToggleAutomation = async (checked: boolean) => {
    setAutomationBusy(true);
    let disableResult: DisableResult | undefined;
    try {
      if (checked) {
        // Upload settings first: enabling with nothing on the server would look
        // switched on while the backend has no API key to send with.
        await syncSettingsToBackend();
        await enableCalendarAutomation();
      } else {
        disableResult = await disableCalendarAutomation();
        clearLastSyncedAt(); // the server copy is purged on disable
      }
      await saveSettings({ ...loadSettings(), calendarAutomation: checked });
      update("calendarAutomation", checked);
      setSyncedAt(getLastSyncedAt());
      notify(
        checked ? t("settings.toast.automationOn") : t("settings.toast.automationOff"),
        checked ? t("settings.toast.automationOnBody") : describeDisableResult(disableResult),
        "success"
      );
    } catch (e) {
      notify(t("settings.toast.automationFailed"), describeAuthError(e), "error");
    } finally {
      setAutomationBusy(false);
    }
  };

  return (
    <div className={styles.panel}>
      <div className={styles.fields} ref={scrollRef} onScroll={updateFade}>
        <Field
          label={t("settings.apiKey.label")}
          required
          validationState={apiKeyError ? "error" : "none"}
          validationMessage={apiKeyError}
          hint={t("settings.apiKey.hint")}
        >
          <Input
            type={showKey ? "text" : "password"}
            value={settings.apiKey}
            onChange={(_, d) => update("apiKey", d.value)}
            placeholder={t("settings.apiKey.placeholder")}
            contentAfter={
              <Button
                appearance="transparent"
                size="small"
                icon={showKey ? <EyeOff16Regular /> : <Eye16Regular />}
                aria-label={showKey ? t("settings.apiKey.hide") : t("settings.apiKey.show")}
                onClick={() => setShowKey((s) => !s)}
              />
            }
          />
        </Field>

        <Field
          label={t("settings.senderId.label")}
          required
          validationState={senderIdError ? "error" : "none"}
          validationMessage={senderIdError}
          hint={t("settings.senderId.hint")}
        >
          <Input value={settings.senderId} onChange={(_, d) => update("senderId", d.value)} />
        </Field>

        <Field label={t("settings.smsType.label")}>
          <Dropdown
            selectedOptions={[settings.smsType]}
            value={optionLabel(t, smsTypeOptions, settings.smsType)}
            onOptionSelect={(_, d) => update("smsType", d.optionValue as SmsType)}
          >
            {smsTypeOptions.map((o) => (
              <Option key={o.value} value={o.value}>
                {t(o.labelKey)}
              </Option>
            ))}
          </Dropdown>
        </Field>

        <Field label={t("settings.encoding.label")}>
          <Dropdown
            selectedOptions={[settings.encoding]}
            value={optionLabel(t, encodingOptions, settings.encoding)}
            onOptionSelect={(_, d) => update("encoding", d.optionValue as Encoding)}
          >
            {encodingOptions.map((o) => (
              <Option key={o.value} value={o.value}>
                {t(o.labelKey)}
              </Option>
            ))}
          </Dropdown>
        </Field>

        <Field label={t("settings.country.label")} hint={t("settings.country.hint")}>
          <Combobox
            freeform
            placeholder={t("settings.country.placeholder")}
            value={countryQuery ?? selectedCountryText}
            // While typing (countryQuery is a string) don't assert the selection,
            // otherwise Fluent forces the input text back to the selected option.
            selectedOptions={countryQuery === null ? [settings.defaultCountry] : []}
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => setCountryQuery(e.target.value)}
            onOptionSelect={(_, d) => {
              if (d.optionValue) {
                update("defaultCountry", d.optionValue);
              }
              setCountryQuery(null);
            }}
          >
            {countryList.length === 0 ? (
              <Option key="none" value="" disabled text={t("settings.country.noMatches")}>
                {t("settings.country.noMatches")}
              </Option>
            ) : (
              countryList.map((c) => (
                <Option key={c.code} value={c.code} text={`${c.name} (${c.dialCode})`}>
                  {c.name} ({c.dialCode})
                </Option>
              ))
            )}
          </Combobox>
        </Field>

        <Field label={t("settings.signature.label")} hint={t("settings.signature.hint")}>
          <Textarea
            value={settings.signature}
            onChange={(_, d) => update("signature", d.value)}
            rows={2}
            resize="vertical"
          />
        </Field>

        <Field
          label={
            <InfoLabel info={<HowToUse styles={styles} />} size="small">
              {t("settings.automation.label")}
            </InfoLabel>
          }
          hint={t("settings.automation.hint")}
        >
          <Switch
            checked={settings.calendarAutomation}
            disabled={automationBusy}
            label={
              automationBusy
                ? t("settings.automation.updating")
                : settings.calendarAutomation
                  ? t("settings.automation.enabled")
                  : t("settings.automation.disabled")
            }
            onChange={(_, d) => (d.checked ? onToggleAutomation(true) : setConfirmDisable(true))}
          />
          {settings.calendarAutomation && (
            <>
              {automation?.state === "problem" ? (
                <Text className={styles.statusProblem}>⚠ {automationDetail(t, automation)}</Text>
              ) : automation?.state === "ok" && automation.at ? (
                <Text className={styles.statusOk}>
                  {t("settings.automation.working", { when: formatDateTime(automation.at) })}
                </Text>
              ) : null}

              <Text className={styles.syncNote}>
                {syncedAt
                  ? t("settings.automation.syncedAt", { when: formatDateTime(syncedAt) })
                  : t("settings.automation.notSynced")}
              </Text>
            </>
          )}
        </Field>
        <ScrollFade visible={showFade} onClick={scrollDown} />
      </div>

      <Dialog open={confirmDisable} onOpenChange={(_, d) => setConfirmDisable(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t("settings.disable.title")}</DialogTitle>
            <DialogContent>
              <Text as="p">{t("settings.disable.scheduled")}</Text>
              <Text as="p">{t("settings.disable.serverCopy")}</Text>
              <Text as="p">{t("settings.disable.sent")}</Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmDisable(false)}>
                {t("settings.disable.cancel")}
              </Button>
              <Button
                appearance="primary"
                disabled={automationBusy}
                onClick={() => {
                  setConfirmDisable(false);
                  void onToggleAutomation(false);
                }}
              >
                {t("settings.disable.confirm")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <div className={styles.footer}>
        <div className={styles.actions}>
          <Button
            className={styles.testButton}
            icon={testing ? <Spinner size="tiny" /> : undefined}
            appearance="secondary"
            onClick={testConnection}
            disabled={!settings.apiKey.trim() || busy}
          >
            {testing ? t("settings.actions.testing") : t("settings.actions.test")}
          </Button>
          <Button
            className={styles.saveButton}
            icon={savingNow ? <Spinner size="tiny" /> : undefined}
            appearance="primary"
            onClick={onSave}
            disabled={busy}
          >
            {savingNow ? t("settings.actions.saving") : t("settings.actions.save")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
